// renderers/trackingDateRenderer.ts
import Handsontable from 'handsontable';
import { differenceInDays, parseISO, isValid } from 'date-fns';
import { COLORS, MAJOR_DELAY_THRESHOLD_DAYS } from '../config/constants';
import type { Shipment, TimeChange } from '../types';
import { formatDate, formatDateWithOffset } from '@/lib/time';

// Helper to create lucide icon SVG
const Icon = (path: string, size: number = 14) =>
  `<svg width="${size}" height="${size}" style="display:inline" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${path}</svg>`;
// Lucide icon paths
const Icons = {
  mapPin: '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>',
  ship: '<path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76"/><path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"/>',
  crane:
    '<path d="M4 1.5h2.5l-.5 1H4.5l-.5-1z"/> <!-- Main boom --> <rect x="2" y="3.5" width="18" height="1.5" rx=".3"/> <!-- Tower --> <rect x="4" y="5" width="2.5" height="17" fill="none" stroke="currentColor" stroke-width=".8"/> <!-- Cross-bracing (simplified) --> <line x1="4" y1="9" x2="6.5" y2="6.5" stroke="currentColor" stroke-width=".6"/> <line x1="4" y1="13" x2="6.5" y2="10.5" stroke="currentColor" stroke-width=".6"/> <line x1="4" y1="17" x2="6.5" y2="14.5" stroke="currentColor" stroke-width=".6"/> <line x1="4" y1="21" x2="6.5" y2="18.5" stroke="currentColor" stroke-width=".6"/> <!-- Tower base --> <rect x="3" y="22" width="11" height="1"/> <!-- Trolley support --> <rect x="17" y="5" width="1" height="5"/> <!-- Cable --> <line x1="17.5" y1="10" x2="17.5" y2="13.5" stroke="currentColor" stroke-width=".6"/> <!-- Container --> <rect x="11" y="13.5" width="12" height="6.5" rx=".5" stroke="currentColor" stroke-width=".8" fill="none"/> <!-- Container corrugations (simplified to 3) --> <line x1="14" y1="14" x2="14" y2="19.5" stroke="currentColor" stroke-width=".6"/> <line x1="17.5" y1="14" x2="17.5" y2="19.5" stroke="currentColor" stroke-width=".6"/> <line x1="21" y1="14" x2="21" y2="19.5" stroke="currentColor" stroke-width=".6"/>',
};

export interface RendererContext {
  shipmentsMap?: Record<string, Shipment>;
}

function getOffsetForField(shipmentData: Shipment, fieldName: string): number | undefined {
  const offsets = shipmentData.timeOffsets;
  if (!offsets) return undefined;

  if (fieldName === 'etd' || fieldName === 'atd') {
    return offsets.etd || offsets.atd;
  }
  if (fieldName === 'eta' || fieldName === 'ata') {
    return offsets.eta || offsets.ata;
  }
  return undefined;
}

function formatDateWithLocationOffset(
  dateString: string | null,
  shipmentData: Shipment,
  fieldName: string,
  formatStr: string = 'dd/MM/yyyy',
): string {
  if (!dateString) return '';
  const offset = getOffsetForField(shipmentData, fieldName) ?? 0;
  return formatDateWithOffset(dateString, offset, formatStr);
}

function getColorsByDiff(diffDays: number, isEstimated: boolean): { bgColor: string; borderColor: string } {
  if (diffDays === 0) {
    return {
      bgColor: isEstimated ? COLORS.estimatedOnTimeBg : COLORS.onTimeBg,
      borderColor: isEstimated ? COLORS.estimatedOnTimeBorder : COLORS.onTimeBorder,
    };
  }

  if (Math.abs(diffDays) > MAJOR_DELAY_THRESHOLD_DAYS) {
    return {
      bgColor: isEstimated ? COLORS.estimatedMajorDelayBg : COLORS.majorDelayBg,
      borderColor: isEstimated ? COLORS.estimatedMajorDelayBorder : COLORS.majorDelayBorder,
    };
  }

  return {
    bgColor: isEstimated ? COLORS.estimatedMinorDelayBg : COLORS.minorDelayBg,
    borderColor: isEstimated ? COLORS.estimatedMinorDelayBorder : COLORS.minorDelayBorder,
  };
}

export function createTrackingDateRenderer(context: RendererContext) {
  return function trackingDateRenderer(
    this: any,
    instance: any,
    td: HTMLTableCellElement,
    row: number,
    col: number,
    prop: string | number,
    value: any,
    cellProperties: any,
  ): HTMLTableCellElement {
    (Handsontable.renderers.BaseRenderer as any).apply(this as any, arguments as any);

    const rowData = instance.getDataAtRow(row);
    const shipmentId = rowData[0];
    const shipmentData = context.shipmentsMap ? context.shipmentsMap[shipmentId] : null;
    const fieldName = prop as string;

    const formattedDate =
      value && shipmentData
        ? formatDateWithLocationOffset(value, shipmentData, fieldName)
        : value
        ? formatDate(value, 'dd/MM/yyyy')
        : '';

    const hasEvents = shipmentData?.eventsCount ?? 0 > 0;

    td.style.cssText = '';
    td.textContent = formattedDate;

    if (!context.shipmentsMap) {
      return td;
    }

    // Gray out cells without events
    if (!hasEvents) {
      td.style.backgroundColor = COLORS.noEventsBg;
      td.style.color = COLORS.noEventsText;
      return td;
    }

    // Handle ETD
    if (fieldName === 'etd') {
      return displayTimeDifferences(
        td,
        value,
        shipmentData!,
        { estimatedField: 'etd', actualField: 'atd' },
        row,
        fieldName,
      );
    }

    // Handle ETA
    if (fieldName === 'eta') {
      return displayTimeDifferences(
        td,
        value,
        shipmentData!,
        { estimatedField: 'eta', actualField: 'ata' },
        row,
        fieldName,
      );
    }

    return td;
  };
}

/**
 * Build tooltip HTML for time changes
 */
export function buildTimeChangeTooltip(
  timeChanges: TimeChange[],
  fieldType: string[],
  index: number,
  shipmentData?: Shipment,
): string {
  const relevantChanges = timeChanges
    .filter((change) => fieldType.includes(change.field))
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  if (relevantChanges.length === 0) {
    return '';
  }

  let tooltipContent = '<table class="tooltip-table">';
  tooltipContent += '<thead><tr>';
  tooltipContent += '<th>Field</th>';
  tooltipContent += '<th>Date</th>';
  tooltipContent += '<th>Location</th>';
  tooltipContent += '<th>Vessel</th>';
  tooltipContent += '<th>Source</th>';
  tooltipContent += '<th>Updated</th>';
  tooltipContent += '</tr></thead>';
  tooltipContent += '<tbody><tr>';

  relevantChanges.forEach((change) => {
    const changeDate = change.datetime ? parseISO(change.datetime) : null;
    const formatStr = change.source === 'carrier_api' ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy';

    const formattedDate =
      isValid(changeDate) && shipmentData
        ? formatDateWithLocationOffset(change.datetime, shipmentData, change.field, formatStr)
        : isValid(changeDate)
        ? formatDate(change.datetime, formatStr)
        : change.datetime;

    tooltipContent += `<td class="tooltip-field">${change.field}</td>`;
    tooltipContent += `<td class="tooltip-date">${formattedDate}</td>`;
    tooltipContent += `<td class="tooltip-location"><span class="tooltip-icon">${Icon(Icons.mapPin, 11)}</span>${
      change.locationName || '-'
    }</td>`;
    tooltipContent += `<td class="tooltip-vessel"><span class="tooltip-icon">${Icon(Icons.ship, 11)}</span>${
      change.vesselName || '-'
    }</td>`;
    tooltipContent += `<td class="tooltip-source">${change.source || '-'}</td>`;
    tooltipContent += `<td class="tooltip-source">${formatDate(change.timestamp, 'dd/MM HH:mm') || '-'}</td>`;
    tooltipContent += '</tr>';
  });

  tooltipContent += '</tbody></table>';
  return tooltipContent;
}

function displayTimeDifferences(
  td: HTMLTableCellElement,
  estimatedDate: string | null,
  shipmentData: Shipment,
  fields: { estimatedField: keyof Shipment; actualField: keyof Shipment },
  index: number,
  fieldName: string,
): HTMLTableCellElement {
  const timeChanges = shipmentData.timeChanges || [];
  const actualDate = shipmentData[fields.actualField] ? parseISO(shipmentData[fields.actualField] as string) : null;

  // Case 1: Only actual date exists (no estimated baseline)
  if (actualDate && !estimatedDate) {
    const icon = Icon(fieldName === 'etd' ? Icons.ship : Icons.crane, 16);
    const { bgColor, borderColor } = getColorsByDiff(0, false); // Green/on-time styling
    const tooltipContent = buildTimeChangeTooltip(
      timeChanges,
      [fields.estimatedField, fields.actualField],
      index,
      shipmentData,
    );

    const displayDate = formatDateWithLocationOffset(
      shipmentData[fields.actualField] as string,
      shipmentData,
      fields.actualField as string,
    );
    td.style.cssText = `
            background-color: ${bgColor};
            border-left: 3px solid ${borderColor};
            position: relative;
            overflow: visible;
        `;
    td.innerHTML = `
            <span style="font-weight: 500;">${icon} ${displayDate}</span>
            <div class="${index > 4 ? 'tracking-tooltip' : 'tracking-tooltip-reversed'}">${tooltipContent}</div>
        `;
    return td;
  }

  // Case 2: No dates at all
  if (!estimatedDate && !actualDate) {
    return td;
  }

  // Case 3: Has estimated date (with or without actual)
  const estimatedDateParsed = parseISO(estimatedDate!);
  const timeChangesEstimated = timeChanges
    .filter((change) => change.field === fields.estimatedField && change.datetime)
    .map((change) => parseISO(change.datetime))
    .filter((date) => isValid(date))
    .sort((a, b) => a.getTime() - b.getTime());

  const firstEstimatedDate = timeChangesEstimated[0] || estimatedDateParsed;
  const lastEstimatedDate = timeChangesEstimated[timeChangesEstimated.length - 1] || estimatedDateParsed;
  const diffDays = actualDate
    ? differenceInDays(actualDate, firstEstimatedDate)
    : differenceInDays(firstEstimatedDate, lastEstimatedDate);
  const { bgColor, borderColor } = getColorsByDiff(diffDays, !actualDate);
  const icon = actualDate ? Icon(fieldName === 'etd' ? Icons.ship : Icons.crane, 16) : '';
  const displayDiff = diffDays !== 0 && !isNaN(diffDays) ? `(${diffDays > 0 ? '+' : ''}${diffDays})` : '';
  const hasCarrierUpdate = timeChanges.some((change) => change.source === 'carrier_api');
  const background = actualDate
    ? `background-color: ${bgColor};`
    : hasCarrierUpdate
    ? `background-color: ${bgColor};`
    : '';
  const border = actualDate
    ? `border-left: 3px solid ${borderColor};`
    : hasCarrierUpdate
    ? `border-left: 3px solid ${borderColor};`
    : '';
  const tooltipContent = buildTimeChangeTooltip(
    timeChanges,
    [fields.estimatedField, fields.actualField],
    index,
    shipmentData,
  );

  const dateToDisplay = (shipmentData[fields.actualField] as string) ?? estimatedDate;
  const fieldToUse = shipmentData[fields.actualField]
    ? (fields.actualField as string)
    : (fields.estimatedField as string);
  const displayDate = formatDateWithLocationOffset(dateToDisplay, shipmentData, fieldToUse);

  td.style.cssText = `
        ${background}
        ${border}
        position: relative;
        overflow: visible;
    `;
  td.innerHTML = `
        <span style="font-weight: 500;">${icon} ${displayDate} ${displayDiff}</span>
        <div class="${index > 4 ? 'tracking-tooltip' : 'tracking-tooltip-reversed'}">${tooltipContent}</div>
    `;
  return td;
}
